import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { defaultPgtoInformacaoCadastroForm, PgtoInformacaoCadastroForm, PgtoInformacaoCadastroSchema } from "../../../schemas/ContratoFinanceiro_cadastro/PgtoInformacaoCadastro_form";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { PgtoContratoFinanceiro } from "../../../../domain/models/pgto_contrato_financeiro";
import { DefaultFormMoneyInput } from "../../../../../../common/components/default_form_money_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";

/**
 * CadastroPagamentoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  pgtoContrato: PgtoContratoFinanceiro | undefined,
  handleOpenChangeHistory: (idTabela: number | null, tipoTabela: LogTipoTabela | null) => void
  onSubmit: (form: PgtoInformacaoCadastroForm) => void,
  onClose: () => void,

};

export const CadastroInformacaoPgtoModal = ({
  open,
  saving,
  pgtoContrato,
  handleOpenChangeHistory,
  onSubmit,
  onClose,

}: Props) => {

  const hookFormPagamento = useForm<PgtoInformacaoCadastroForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: defaultPgtoInformacaoCadastroForm,
    resolver: zodResolver(PgtoInformacaoCadastroSchema),

  });

  const handleSubmit = useCallback((form: PgtoInformacaoCadastroForm) => {
    if (!saving) onSubmit(form);
  }, [saving, onSubmit]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);


  useEffect(() => {
    if (pgtoContrato) {
      hookFormPagamento.setValue('idPgtoContratoFinanceiro', pgtoContrato.idPgtoContratoFinanceiro);
      hookFormPagamento.setValue('dataReceber', pgtoContrato.dataReceber);
      hookFormPagamento.setValue('dataRecebido', pgtoContrato.dataRecebido);
      hookFormPagamento.setValue('valorReceber', pgtoContrato.valorReceber);
      hookFormPagamento.setValue('valorRecebido', pgtoContrato.valorRecebido);
      hookFormPagamento.setValue('observacao', pgtoContrato.observacao);

      if (pgtoContrato.entrada && !pgtoContrato.parcelaSecundaria) {
        hookFormPagamento.setValue('tipo', 1);
      } else if (pgtoContrato.parcelaSecundaria && !pgtoContrato.entrada) {
        hookFormPagamento.setValue('tipo', 2);
      } else if (!pgtoContrato.entrada && !pgtoContrato.parcelaSecundaria) {
        hookFormPagamento.setValue('tipo', 0);
      }
    } else {
      hookFormPagamento.reset();
    }
  }, [pgtoContrato, hookFormPagamento])

  return (

    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookFormPagamento.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>

      <DialogTitle>
        Informações pagamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>

              <DefaultFormAutocomplete
                name='tipo'
                label='Tipo'
                getOptionKey={x => x.value}
                getOptionLabel={x => x.label}
                readOnly={pgtoContrato?.entrada === false && pgtoContrato?.parcelaSecundaria === false}
                options={[
                  { label: 'Parcela', value: 0 },
                  { label: 'Entrada', value: 1 },
                  { label: 'Parcela Secundária', value: 2 },
                ]}
                reactHookForm={hookFormPagamento}
              />

              <DefaultFormMoneyInput
                name='valorReceber'
                label='Valor Receber'
                readOnly={true}
                reactHookForm={hookFormPagamento}
              />

              <FormDatePickerInput
                name='dataReceber'
                label='Data Receber'
                readOnly={pgtoContrato?.status === true}
                reactHookForm={hookFormPagamento}
              />

              <DefaultFormMoneyInput
                name='valorRecebido'
                label='Valor Recebido:'
                readOnly={true}
                reactHookForm={hookFormPagamento}
              />

              <FormDatePickerInput
                name='dataRecebido'
                label='Data Recebido'
                readOnly={true}
                reactHookForm={hookFormPagamento}
              />

              <DefaultFormInput
                name='observacao'
                label='Observações'
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookFormPagamento}
              />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex px-4 pb-4 gap-4">

          {
            pgtoContrato &&
            <div className="flex flex-row gap-2">
              <PrimaryButton
                buttonColor="yellow"
                onClick={() => handleOpenChangeHistory(pgtoContrato?.idPgtoContratoFinanceiro ?? null, LogTipoTabela.PGTO_CONTRATO_FINANCEIRO)}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            </div>
          }

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}